"use client"

import { useState } from "react"
import { useCart } from "@/lib/cart-context"
import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ConfirmationDialog } from "@/components/confirmation-dialog"
import { ShoppingCart, Trash2, Plus, Minus, Package, ArrowRight } from "lucide-react"
import { toast } from "sonner"
import Link from "next/link"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { buildFullImageUrl } from "@/lib/utils"

export default function CartPage() {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5000"
    const router = useRouter()
    const { cart, removeFromCart, updateQuantity, clearCart, getTotalItems, getTotalPrice } = useCart()
    const [showClearConfirm, setShowClearConfirm] = useState(false)
    const [itemToRemove, setItemToRemove] = useState<{ id: string; name: string } | null>(null)

    const handleRemove = (productId: string | undefined, productName: string) => {
        if (!productId) return
        setItemToRemove({ id: productId, name: productName })
    }

    const confirmRemove = () => {
        if (!itemToRemove) return
        removeFromCart(itemToRemove.id)
        toast.success(`${itemToRemove.name} retiré du panier`)
        setItemToRemove(null)
    }

    const handleUpdateQuantity = (productId: string | undefined, newQuantity: number, maxStock: number | null | undefined) => {
        if (!productId) return

        // Vérifier le stock seulement s'il est géré (pas null)
        if (maxStock !== null && maxStock !== undefined && newQuantity > maxStock) {
            toast.error(`Stock maximum: ${maxStock}`)
            return
        }

        updateQuantity(productId, newQuantity)
    }

    const handleClearCart = () => {
        setShowClearConfirm(true)
    }

    const confirmClearCart = () => {
        clearCart()
        toast.success("Panier vidé")
        setShowClearConfirm(false)
    }

    const handleCheckout = () => {
        if (cart.length === 0) {
            toast.error("Votre panier est vide")
            return
        }

        // Préparer les données pour la commande
        const orderItems = cart.map(item => ({
            productId: item.product._id,
            productName: item.product.name,
            quantity: item.quantity,
            price: item.price,
            originalPrice: item.originalPrice,
            promotionId: item.promotionId
        }))

        // Stocker dans sessionStorage pour la page de commande
        sessionStorage.setItem("cart", JSON.stringify(orderItems))

        // Rediriger vers la page de commande
        router.push("/commande")
    }

    const totalItems = getTotalItems()
    const totalPrice = getTotalPrice()
    const totalOriginalPrice = cart.reduce((sum, item) => sum + (item.originalPrice || item.price) * item.quantity, 0)
    const totalSavings = totalOriginalPrice - totalPrice

    return (
        <div className="flex min-h-screen flex-col">
            <Navbar />
            <main className="flex-1 bg-gradient-to-b from-primary/5 to-background">
                <div className="container mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="mb-8 flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-foreground">Mon Panier</h1>
                            <p className="mt-2 text-muted-foreground">
                                {totalItems} {totalItems > 1 ? "articles" : "article"}
                            </p>
                        </div>
                        {cart.length > 0 && (
                            <Button variant="outline" onClick={handleClearCart} className="gap-2">
                                <Trash2 className="h-4 w-4" />
                                Vider le panier
                            </Button>
                        )}
                    </div>

                    {/* Empty State */}
                    {cart.length === 0 ? (
                        <Card className="border-dashed">
                            <CardContent className="flex flex-col items-center justify-center py-16">
                                <div className="rounded-full bg-primary/10 p-6 mb-4">
                                    <ShoppingCart className="h-12 w-12 text-primary" />
                                </div>
                                <h2 className="text-2xl font-semibold mb-2">Votre panier est vide</h2>
                                <p className="text-muted-foreground mb-6 text-center max-w-md">
                                    Découvrez nos produits et ajoutez-les à votre panier
                                </p>
                                <Button asChild>
                                    <Link href="/boutique" className="gap-2">
                                        <Package className="h-4 w-4" />
                                        Découvrir nos produits
                                    </Link>
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid gap-8 lg:grid-cols-3">
                            {/* Cart Items */}
                            <div className="lg:col-span-2 space-y-4">
                                {cart.map((item) => (
                                    <Card key={item.product._id}>
                                        <CardContent className="p-4">
                                            <div className="flex gap-4">
                                                {/* Product Image */}
                                                <div className="relative h-24 w-24 flex-shrink-0 overflow-hidden rounded-lg bg-muted">
                                                    {(() => {
                                                        const imageUrl = buildFullImageUrl(item.product.images?.[0])
                                                        return imageUrl ? (
                                                            <Image
                                                                src={imageUrl}
                                                                alt={item.product.name}
                                                                fill
                                                                unoptimized
                                                                className="object-cover"
                                                            />
                                                        ) : (
                                                            <div className="flex h-full items-center justify-center">
                                                                <Package className="h-8 w-8 text-muted-foreground/50" />
                                                            </div>
                                                        )
                                                    })()}
                                                </div>

                                                {/* Product Info */}
                                                <div className="flex flex-1 flex-col">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <Link
                                                                href={`/produits/${item.product._id}`}
                                                                className="font-semibold hover:text-primary transition line-clamp-2"
                                                            >
                                                                {item.product.name}
                                                            </Link>
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                {item.product.category}
                                                            </p>

                                                            {/* Price */}
                                                            <div className="mt-2 flex items-baseline gap-2">
                                                                <span className="text-lg font-bold text-primary">
                                                                    {item.price.toFixed(0)} FCFA
                                                                </span>
                                                                {item.originalPrice && item.originalPrice > item.price && (
                                                                    <>
                                                                        <span className="text-sm text-muted-foreground line-through">
                                                                            {item.originalPrice.toFixed(0)} FCFA
                                                                        </span>
                                                                        <Badge variant="destructive" className="text-xs">
                                                                            -{Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)}%
                                                                        </Badge>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Remove Button */}
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleRemove(item.product._id, item.product.name)}
                                                            className="text-destructive hover:text-destructive"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>

                                                    {/* Quantity Controls */}
                                                    <div className="mt-4 flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={() => handleUpdateQuantity(item.product._id, item.quantity - 1, item.product.stock)}
                                                            >
                                                                <Minus className="h-4 w-4" />
                                                            </Button>
                                                            <span className="w-12 text-center font-medium">
                                                                {item.quantity}
                                                            </span>
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={() => handleUpdateQuantity(item.product._id, item.quantity + 1, item.product.stock)}
                                                                disabled={
                                                                    item.product.stock !== null &&
                                                                    item.product.stock !== undefined &&
                                                                    item.quantity >= item.product.stock
                                                                }
                                                            >
                                                                <Plus className="h-4 w-4" />
                                                            </Button>
                                                        </div>

                                                        {/* Subtotal */}
                                                        <div className="text-right">
                                                            <p className="text-sm text-muted-foreground">Sous-total</p>
                                                            <p className="text-lg font-bold">
                                                                {(item.price * item.quantity).toFixed(0)} FCFA
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Stock Warning */}
                                                    {item.product.stock !== null &&
                                                        item.product.stock !== undefined &&
                                                        item.quantity >= item.product.stock && (
                                                            <p className="mt-2 text-xs text-orange-600">
                                                                Stock maximum atteint ({item.product.stock} disponible{item.product.stock > 1 ? "s" : ""})
                                                            </p>
                                                        )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            {/* Order Summary */}
                            <div className="lg:col-span-1">
                                <Card className="sticky top-24">
                                    <CardContent className="p-6">
                                        <h2 className="text-xl font-bold mb-4">Résumé de la commande</h2>

                                        <div className="space-y-3 mb-4 pb-4 border-b">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">Articles ({totalItems})</span>
                                                <span className="font-medium">{totalOriginalPrice.toFixed(0)} FCFA</span>
                                            </div>

                                            {totalSavings > 0 && (
                                                <div className="flex justify-between text-sm text-green-600">
                                                    <span>Économies</span>
                                                    <span className="font-medium">-{totalSavings.toFixed(0)} FCFA</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex justify-between text-lg font-bold mb-6">
                                            <span>Total</span>
                                            <span className="text-primary">{totalPrice.toFixed(0)} FCFA</span>
                                        </div>

                                        <Button
                                            className="w-full gap-2"
                                            size="lg"
                                            onClick={handleCheckout}
                                        >
                                            Passer la commande
                                            <ArrowRight className="h-4 w-4" />
                                        </Button>

                                        <Button
                                            variant="outline"
                                            className="w-full mt-3"
                                            asChild
                                        >
                                            <Link href="/boutique">
                                                Continuer mes achats
                                            </Link>
                                        </Button>

                                        {totalSavings > 0 && (
                                            <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                                                <p className="text-sm text-green-700 dark:text-green-300 text-center font-medium">
                                                    🎉 Vous économisez {totalSavings.toFixed(0)} FCFA !
                                                </p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <Footer />

            {/* Confirmation Dialogs */}
            <ConfirmationDialog
                open={showClearConfirm}
                title="Vider le panier"
                description="Êtes-vous sûr de vouloir vider votre panier ? Cette action est irréversible."
                onConfirm={confirmClearCart}
                onCancel={() => setShowClearConfirm(false)}
                confirmText="Vider le panier"
                cancelText="Annuler"
                isDangerous
            />

            <ConfirmationDialog
                open={!!itemToRemove}
                title="Retirer du panier"
                description={`Êtes-vous sûr de vouloir retirer "${itemToRemove?.name}" de votre panier ?`}
                onConfirm={confirmRemove}
                onCancel={() => setItemToRemove(null)}
                confirmText="Retirer"
                cancelText="Annuler"
                isDangerous
            />
        </div>
    )
}
